"use strict";

(function () {
  const STORAGE_KEY = "pakari_favoritelist";
  // 260601要件：最大登録数。301件目以降は登録不可。
  const MAX_FAVORITES = 300;
  // 並び替え選択の保持先・遅延読み込みの1バッチ件数
  const SORT_KEY = "pakari_favoritelist_sort";
  const DEFAULT_SORT = "date_desc";
  const BATCH = 12;
  // 260612要件A：_thumbnail対応。既存キャッシュの画像を一度だけ再取得させる移行バージョン
  const STORE_VER_KEY = "pakari_favoritelist_ver";
  const STORE_VER = "2";

  function readStore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeStore(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // 容量超過などは無視
    }
  }

  // 260612要件A：_thumbnail対応。旧バージョンのキャッシュは画像/価格を破棄して
  // 初回表示時に一度だけ再取得させる（再取得で _thumbnail 画像が選択される）。
  function migrateStore() {
    try {
      if (localStorage.getItem(STORE_VER_KEY) === STORE_VER) return;
      const items = readStore();
      items.forEach((it) => {
        delete it.image;
        delete it.price;
        delete it.compare_at_price;
      });
      writeStore(items);
      localStorage.setItem(STORE_VER_KEY, STORE_VER);
    } catch (e) {}
  }
  migrateStore();

  function has(id) {
    return readStore().some((item) => String(item.id) === String(id));
  }

  function toggle(id, handle) {
    const items = readStore();
    const idx = items.findIndex((item) => String(item.id) === String(id));
    if (idx >= 0) {
      items.splice(idx, 1);
    } else {
      // 260601要件：上限300件。到達時は登録せずメッセージ表示（ハートは非選択のまま）
      if (items.length >= MAX_FAVORITES) {
        try {
          window.alert("お気に入りの登録上限は300件です");
        } catch (e) {}
        return false;
      }
      items.unshift({ id: id, handle: handle, addedAt: Date.now() });
    }
    writeStore(items);
    const ids = items.map((i) => i.id);
    document.dispatchEvent(
      new CustomEvent("pakari:favoritelist:update", { detail: { ids: ids } })
    );
    if (typeof publish === "function") {
      try {
        publish("favoritelist-update", { ids: ids });
      } catch (e) {}
    }
    return idx < 0;
  }

  class FavoritelistButton extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.applyState(has(this.productId));
      this.addEventListener("click", this.onClick.bind(this));
      this._onStorage = this.onStorage.bind(this);
      this._onUpdate = this.onUpdate.bind(this);
      window.addEventListener("storage", this._onStorage);
      document.addEventListener("pakari:favoritelist:update", this._onUpdate);
    }

    disconnectedCallback() {
      window.removeEventListener("storage", this._onStorage);
      document.removeEventListener("pakari:favoritelist:update", this._onUpdate);
    }

    onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const active = toggle(this.productId, this.dataset.productHandle);
      this.applyState(active);
    }

    onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      this.applyState(has(this.productId));
    }

    onUpdate() {
      this.applyState(has(this.productId));
    }

    applyState(active) {
      this.dataset.active = active ? "true" : "false";
      this.setAttribute("aria-pressed", active ? "true" : "false");
      this.setAttribute(
        "aria-label",
        active ? "お気に入りから削除" : "お気に入りに追加"
      );
    }
  }

  if (!customElements.get("favoritelist-button")) {
    customElements.define("favoritelist-button", FavoritelistButton);
  }

  class FavoritelistCounter extends HTMLElement {
    connectedCallback() {
      this.badge = this.querySelector(".favoritelist-counter__badge");
      this.render();
      this._onStorage = this.onStorage.bind(this);
      this._onUpdate = this.onUpdate.bind(this);
      window.addEventListener("storage", this._onStorage);
      document.addEventListener("pakari:favoritelist:update", this._onUpdate);
    }

    disconnectedCallback() {
      window.removeEventListener("storage", this._onStorage);
      document.removeEventListener("pakari:favoritelist:update", this._onUpdate);
    }

    onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      this.render();
    }

    onUpdate() {
      this.render();
    }

    render() {
      if (!this.badge) return;
      const count = readStore().length;
      this.badge.textContent = String(count);
      this.badge.dataset.count = String(count);
      if (count > 0) {
        this.badge.removeAttribute("hidden");
      } else {
        this.badge.setAttribute("hidden", "");
      }
    }
  }

  if (!customElements.get("favoritelist-counter")) {
    customElements.define("favoritelist-counter", FavoritelistCounter);
  }

  class FavoritelistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector("[data-grid]");
      this.emptyEl = this.querySelector("[data-empty]");
      this.toolbarEl = this.querySelector("[data-toolbar]");
      this.countNumEl = this.querySelector("[data-count-num]");
      this.sortSelect = this.querySelector("[data-sort]");
      this.sentinel = this.querySelector("[data-sentinel]");
      // productId(String) -> カード要素。増分リコンサイルで削除・追加時に全カードを作り直さない。
      this.cardMap = new Map();
      // handle -> product.js データのメモリキャッシュ（同一セッション内のfetch削減）
      this.productCache = new Map();
      this._rendering = false;
      this._pending = false;
      this.sortedItems = [];
      this.renderedCount = 0;
      this.sortKey = this.readSort();
      if (this.sortSelect) {
        this.sortSelect.value = this.sortKey;
        this.sortSelect.addEventListener("change", this.onSortChange.bind(this));
      }
      // 遅延読み込み：センチネルが視界に入るたび次バッチを描画（トップページ＝IntersectionObserver準拠）
      if (this.sentinel && "IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((en) => en.isIntersecting)) this.loadMore();
          },
          { rootMargin: "0px 0px 400px 0px" }
        );
        this.observer.observe(this.sentinel);
      }
      this.render();
      this._onStorage = this.onStorage.bind(this);
      this._onUpdate = this.onUpdate.bind(this);
      window.addEventListener("storage", this._onStorage);
      document.addEventListener("pakari:favoritelist:update", this._onUpdate);
    }

    disconnectedCallback() {
      window.removeEventListener("storage", this._onStorage);
      document.removeEventListener("pakari:favoritelist:update", this._onUpdate);
      if (this.observer) this.observer.disconnect();
    }

    onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      this.render();
    }

    onUpdate() {
      this.render();
    }

    readSort() {
      try {
        return localStorage.getItem(SORT_KEY) || DEFAULT_SORT;
      } catch (e) {
        return DEFAULT_SORT;
      }
    }

    writeSort(v) {
      try {
        localStorage.setItem(SORT_KEY, v);
      } catch (e) {}
    }

    onSortChange(e) {
      this.sortKey = e.target.value || DEFAULT_SORT;
      this.writeSort(this.sortKey);
      // 並び替えは意図的操作。グリッドを作り直して先頭バッチから再描画（キャッシュ済みは即時）
      this.cardMap.forEach((el) => el.remove());
      this.cardMap.clear();
      this.renderedCount = 0;
      this.render();
    }

    async render() {
      if (this._rendering) {
        this._pending = true;
        return;
      }
      this._rendering = true;
      try {
        await this.reconcile();
      } finally {
        this._rendering = false;
        if (this._pending) {
          this._pending = false;
          this.render();
        }
      }
    }

    async reconcile() {
      let items = readStore().filter((item) => item.handle);

      // 件数表示（n / 300）。0件は空状態を表示しツールバー非表示
      this.updateCount(items.length);
      if (items.length === 0) {
        this.cardMap.forEach((el) => el.remove());
        this.cardMap.clear();
        this.renderedCount = 0;
        this.sortedItems = [];
        if (this.toolbarEl) this.toolbarEl.setAttribute("hidden", "");
        this.emptyEl.removeAttribute("hidden");
        return;
      }
      this.emptyEl.setAttribute("hidden", "");
      if (this.toolbarEl) this.toolbarEl.removeAttribute("hidden");

      // 価格ソート時は価格が必要。未キャッシュ分を取得・書き戻ししてから整列
      if (this.sortKey === "price_asc" || this.sortKey === "price_desc") {
        await this.ensurePrices(items);
        items = readStore().filter((item) => item.handle);
      }

      this.sortedItems = this.sortItems(items);

      // store から消えたカードのみ削除（増分リコンサイル＝×削除時のチラつき防止）
      const wantedSet = new Set(this.sortedItems.map((it) => String(it.id)));
      this.cardMap.forEach((el, id) => {
        if (!wantedSet.has(id)) {
          el.remove();
          this.cardMap.delete(id);
        }
      });

      // 描画件数を確定（最低でも先頭バッチ、最大でも総数）
      if (this.renderedCount < BATCH) this.renderedCount = BATCH;
      if (this.renderedCount > this.sortedItems.length) {
        this.renderedCount = this.sortedItems.length;
      }

      await this.renderUpTo(this.renderedCount);

      // まだ未描画が残る場合、センチネルを再観測して交差状態を再評価する。
      // （バッチ描画後もセンチネルが視界に残るケースで次バッチが発火しない取りこぼしを防ぐ）
      if (
        this.observer &&
        this.sentinel &&
        this.renderedCount < this.sortedItems.length
      ) {
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }
    }

    // sortKey に従って items を並び替えた新配列を返す
    sortItems(items) {
      const arr = items.slice();
      const num = (v) => (v == null ? 0 : Number(v));
      switch (this.sortKey) {
        case "date_asc":
          arr.sort((a, b) => num(a.addedAt) - num(b.addedAt));
          break;
        case "price_asc":
          arr.sort((a, b) => num(a.price) - num(b.price));
          break;
        case "price_desc":
          arr.sort((a, b) => num(b.price) - num(a.price));
          break;
        case "date_desc":
        default:
          arr.sort((a, b) => num(b.addedAt) - num(a.addedAt));
          break;
      }
      return arr;
    }

    // 先頭 count 件を描画（キャッシュ優先、未取得のみそのバッチ分だけfetch）。順序は sortedItems 準拠。
    async renderUpTo(count) {
      const slice = this.sortedItems.slice(0, count);

      await Promise.all(
        slice.map(async (item) => {
          const id = String(item.id);
          if (this.cardMap.has(id)) return;
          const cacheHit = this.entryHasCache(item);
          let data = cacheHit ? this.fromCache(item) : null;
          if (!data) {
            const product = await this.fetchProduct(item.handle);
            if (!product) return;
            data = this.fromProductJs(product);
            this.writeBackCache(item.id, data); // 次回アクセスはfetch不要＝ラグ解消
          }
          this.cardMap.set(id, this.renderCard(data));
          // 260708：キャッシュ表示分はバックグラウンドで最新化（在庫切れ/割引をカードへ反映）
          if (cacheHit) this.revalidate(item, data);
        })
      );

      // 並び順に再配置（既存ノードのappendChildは再描画されず順序のみ変更）。センチネルは末尾へ。
      slice.forEach((item) => {
        const card = this.cardMap.get(String(item.id));
        if (card) this.grid.appendChild(card);
      });
      if (this.sentinel && this.grid.parentNode) {
        this.grid.parentNode.appendChild(this.sentinel);
      }
    }

    // 260708：表示後にバックグラウンドで最新データを取得し、価格/在庫等に差分があれば
    // キャッシュを更新してカードを差し替える（stale-while-revalidate）。
    // fetchProduct は productCache でページ内重複を抑制。差分が無ければ書き込み無し。
    async revalidate(item, oldData) {
      const product = await this.fetchProduct(item.handle);
      if (!product) return;
      const fresh = this.fromProductJs(product);
      const changed =
        fresh.price !== oldData.price ||
        fresh.compare_at_price !== oldData.compare_at_price ||
        fresh.available !== oldData.available ||
        String(fresh.variantId) !== String(oldData.variantId) ||
        fresh.title !== oldData.title ||
        fresh.image !== oldData.image;
      if (!changed) return;
      this.writeBackCache(item.id, fresh);
      const id = String(item.id);
      const oldCard = this.cardMap.get(id);
      const newCard = this.renderCard(fresh);
      this.cardMap.set(id, newCard);
      if (oldCard && oldCard.parentNode) {
        oldCard.parentNode.replaceChild(newCard, oldCard);
      }
    }

    loadMore() {
      if (this._rendering) return;
      if (this.renderedCount >= this.sortedItems.length) return;
      this.renderedCount = Math.min(
        this.renderedCount + BATCH,
        this.sortedItems.length
      );
      this.render();
    }

    updateCount(n) {
      if (this.countNumEl) this.countNumEl.textContent = String(n);
    }

    entryHasCache(item) {
      return item && item.title != null && item.price != null;
    }

    fromCache(item) {
      return {
        id: item.id,
        handle: item.handle,
        title: item.title,
        image: item.image || "",
        price: item.price,
        compare_at_price: item.compare_at_price != null ? item.compare_at_price : null,
        available: !!item.available,
        variantId: item.variantId != null ? item.variantId : null,
      };
    }

    // 260612要件A：商品画像配列から _thumbnail を含むファイルを優先選択
    pickThumbnailImage(p) {
      const imgs = (p && p.images) || [];
      for (let i = 0; i < imgs.length; i++) {
        const src = imgs[i];
        if (typeof src !== "string") continue;
        const base = src.split("/").pop().split("?")[0].split(".")[0];
        if (base.indexOf("_thumbnail") !== -1) return src;
      }
      return null;
    }

    fromProductJs(p) {
      const firstVariant = p.variants && p.variants[0];
      return {
        id: p.id,
        handle: p.handle,
        title: p.title,
        image: this.pickThumbnailImage(p) || p.featured_image || (p.images && p.images[0]) || "",
        price: p.price,
        compare_at_price: p.compare_at_price != null ? p.compare_at_price : null,
        available: !!(firstVariant && firstVariant.available),
        variantId: firstVariant ? String(firstVariant.id) : null,
      };
    }

    async fetchProduct(handle) {
      let product = this.productCache.get(handle);
      if (product) return product;
      product = await fetch("/products/" + encodeURIComponent(handle) + ".js")
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (product) this.productCache.set(handle, product);
      return product;
    }

    // localStorage の該当エントリへ商品データを書き戻す（順序・addedAtは保持）
    writeBackCache(id, data) {
      const items = readStore();
      const idx = items.findIndex((it) => String(it.id) === String(id));
      if (idx < 0) return;
      items[idx] = Object.assign({}, items[idx], {
        title: data.title,
        image: data.image,
        price: data.price,
        compare_at_price: data.compare_at_price,
        available: data.available,
        variantId: data.variantId,
      });
      writeStore(items);
    }

    // 価格ソート用：price未保持のエントリをfetchして書き戻す
    async ensurePrices(items) {
      const missing = items.filter((it) => it.price == null);
      await Promise.all(
        missing.map(async (item) => {
          const product = await this.fetchProduct(item.handle);
          if (!product) return;
          this.writeBackCache(item.id, this.fromProductJs(product));
        })
      );
    }

    renderCard(product) {
      // 260529要件：カートボタンはカードの外側に配置するためラッパーで包む
      const wrap = document.createElement("div");
      wrap.className = "favoritelist-page__card-wrap";

      const card = document.createElement("div");
      card.className = "favoritelist-page__card";

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "favoritelist-page__card-remove";
      removeBtn.setAttribute("aria-label", "お気に入りから削除");
      removeBtn.textContent = "×";
      removeBtn.dataset.productId = String(product.id);
      removeBtn.dataset.productHandle = product.handle;
      removeBtn.addEventListener("click", this.onRemove.bind(this));
      card.appendChild(removeBtn);

      const imageLink = document.createElement("a");
      imageLink.href = "/products/" + product.handle;
      imageLink.className = "favoritelist-page__card-image-link";
      const img = document.createElement("img");
      img.className = "favoritelist-page__card-image";
      img.src = product.image || "";
      img.alt = product.title || "";
      img.loading = "lazy";
      imageLink.appendChild(img);

      // 260623要件：サムネイル左下に割引バッジ・在庫切れバッジを表示
      // コレクション等の商品カードと同一表示にするため共通クラス(.product_badge/.badge)を再利用
      const badgeOnSale = product.compare_at_price && product.compare_at_price > product.price;
      const isSoldOut = product.available === false;
      if (badgeOnSale || isSoldOut) {
        const badgeWrap = document.createElement("div");
        badgeWrap.className = "product_badge";
        if (isSoldOut) {
          const soldBadge = document.createElement("span");
          soldBadge.className = "badge soldbadge";
          const soldText = document.createElement("span");
          soldText.textContent = "在庫切れ";
          soldBadge.appendChild(soldText);
          badgeWrap.appendChild(soldBadge);
        }
        if (badgeOnSale) {
          const percent = Math.round(
            ((product.compare_at_price - product.price) * 100) / product.compare_at_price
          );
          const saleBadge = document.createElement("span");
          saleBadge.className = "badge salebadge";
          const saleText = document.createElement("span");
          saleText.textContent = percent + "%OFF";
          saleBadge.appendChild(saleText);
          badgeWrap.appendChild(saleBadge);
        }
        imageLink.appendChild(badgeWrap);
      }
      card.appendChild(imageLink);

      const body = document.createElement("div");
      body.className = "favoritelist-page__card-body";

      const title = document.createElement("a");
      title.href = "/products/" + product.handle;
      title.className = "favoritelist-page__card-title";
      title.textContent = product.title;
      body.appendChild(title);

      const priceWrap = document.createElement("div");
      priceWrap.className = "favoritelist-page__card-prices";
      const isOnSale = product.compare_at_price && product.compare_at_price > product.price;
      const priceMain = document.createElement("span");
      priceMain.className = isOnSale
        ? "favoritelist-page__card-price favoritelist-page__card-price--sale"
        : "favoritelist-page__card-price";
      priceMain.textContent = this.formatMoney(product.price);
      priceWrap.appendChild(priceMain);
      if (isOnSale) {
        const priceCompare = document.createElement("s");
        priceCompare.className = "favoritelist-page__card-price--compare";
        priceCompare.textContent = this.formatMoney(product.compare_at_price);
        priceWrap.appendChild(priceCompare);
      }
      body.appendChild(priceWrap);

      const button = document.createElement("button");
      button.type = "button";
      button.className = "favoritelist-page__card-button";
      button.textContent = "カートに入れる";
      if (product.available && product.variantId) {
        button.dataset.variantId = String(product.variantId);
        button.dataset.productId = String(product.id);
        button.dataset.productHandle = product.handle;
        button.addEventListener("click", this.onAddToCart.bind(this));
      } else {
        button.disabled = true;
        button.textContent = "在庫切れ";
      }
      card.appendChild(body);
      wrap.appendChild(card);
      // カートボタンはカード(.favoritelist-page__card)の外側に配置
      wrap.appendChild(button);
      return wrap;
    }

    formatMoney(priceInCents) {
      const yen = Math.round(Number(priceInCents) / 100);
      return "¥" + yen.toLocaleString("ja-JP");
    }

    onRemove(e) {
      e.preventDefault();
      e.stopPropagation();
      const btn = e.currentTarget;
      toggle(btn.dataset.productId, btn.dataset.productHandle);
    }

    async onAddToCart(e) {
      const btn = e.currentTarget;
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = "追加中...";
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1 }),
        });
        if (res.ok) {
          // 260529要件：カート追加後もお気に入りからは削除しない（toggleは呼ばない）。
          location.href = "/cart";
        } else {
          throw new Error("add to cart failed");
        }
      } catch (err) {
        btn.textContent = "失敗";
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 1500);
      }
    }
  }

  if (!customElements.get("favoritelist-page")) {
    customElements.define("favoritelist-page", FavoritelistPage);
  }
})();
